import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, computed, flow, observable, runInAction } from "mobx";
import { ECheckoutCustomerType, EProductPlanVisibility, routeLocationPaddleCheckoutPatch, routeLocationPaddleIncompleteCheckoutIsCompleteGet, routeLocationPaddleIncompleteCheckoutPatch, routeLocationPaddleIncompleteCheckoutPost } from "@devowl-wp/api-real-commerce";
import { RealCommerceError } from "../../../exceptions/realCommerceError.js";
import { getAffiliateFromStorage } from "../../../others/getAffiliateFromStorage.js";
import { __ } from "../../../utils/i18n.js";
import { realCommerceRequest } from "../../../utils/realCommerceRequest.js";
import { mergeIntoMemorized } from "../hooks/useRecoverableFormV2.js";
import { CheckoutStep } from "../store/CheckoutV2Store.js";
const RETRY_CHECKOUT_PATCH_RETRIES = 29;
const RETRY_CHECKOUT_PATCH_WAIT = 3000;
class Transaction {
    get isRecovered() {
        return !!this.recoveredIncompleteCheckout;
    }
    get store() {
        return this.product.store;
    }
    get awaitingPaymentRetryImageAndText() {
        const urlPrefix = `${this.store.rootStore.optionStore.publicUrl}images/delayed-payment/`;
        const { awaitingPaymentRetry } = this;
        if (awaitingPaymentRetry <= 3) {
            return [
                undefined,
                __("We are waiting for the payment confirmation. Be patient for a moment...")
            ];
        } else if (awaitingPaymentRetry <= 6) {
            return [
                `${urlPrefix}1.jpg`,
                __("Confirmation of payment takes longer than usual. Please have a little patience...")
            ];
        } else if (awaitingPaymentRetry <= 10) {
            return [
                `${urlPrefix}2.jpg`,
                __("We have found the cable through which the payment must. Now it just has to go through...")
            ];
        } else if (awaitingPaymentRetry <= 15) {
            return [
                `${urlPrefix}3.jpg`,
                __("Knot in the cable found! Now your payment can finally travel through the vastness of the internet...")
            ];
        } else {
            return [
                `${urlPrefix}4.jpg`,
                __("Your payment is on its way. Hopefully it will be confirmed soon...")
            ];
        }
    }
    /**
     * Whether a coupon is currently attached to the incomplete checkout.
     * Drives the iso package's coupon-applied gate; the absolute discount
     * amount is derived inside `computeOrderSummary` from the per-buyer
     * baselines.
     *
     * Reads from the observable `incompleteCheckout` snapshot (refreshed by
     * `syncCouponWithIncompleteCheckout` on every Paddle / iframe coupon
     * change) rather than the private `lastAppliedCoupon` field — that
     * field is non-observable, so a MobX-tracked getter on it would not
     * recompute when Paddle's `applyNewPrices` updates the coupon.
     */ get hasAppliedCoupon() {
        return !!this.incompleteCheckout?.checkout.coupon;
    }
    constructor(product, recovered){
        /**
     * When doing `applyPayment` and the payment is not yet processed on the eCommerce system,
     * we will retry every x seconds. This variable holds the number of the current attempt.
     */ this.awaitingPaymentRetry = 0;
        /**
     * Save the last applied coupon of this transaction. This is necessary so we
     * can automatically safe the applied coupon to our backend or delete it.
     */ this.lastAppliedCoupon = null;
        /**
     * Apply the coupon and sync it with the incomplete checkout.
     *
     * @param data
     */ this.syncCouponWithIncompleteCheckout = flow(function*(coupon, throwIfError) {
            if (this.lastAppliedCoupon === coupon) {
                // Already synced
                return;
            }
            this.lastAppliedCoupon = coupon;
            try {
                const response = yield realCommerceRequest({
                    location: routeLocationPaddleIncompleteCheckoutPatch,
                    request: {
                        checkout: {
                            uuid: this.incompleteCheckout.checkout.uuid,
                            coupon
                        }
                    }
                });
                // Capture the re-evaluated server state — most importantly
                // `cashbackPromotion`, which the backend recomputes when the
                // coupon changes (cashback campaigns can be bound to a specific
                // coupon code). Without this merge the right-column Order
                // Summary keeps showing the post-identification snapshot and
                // never reflects an iframe-side coupon-add or coupon-remove.
                if (this.incompleteCheckout) {
                    this.incompleteCheckout = {
                        ...this.incompleteCheckout,
                        checkout: response.checkout
                    };
                }
                this.lastSyncCouponWithIncompleteCheckoutError = undefined;
            } catch (e) {
                this.lastSyncCouponWithIncompleteCheckoutError = new RealCommerceError(e);
                if (throwIfError) {
                    throw this.lastSyncCouponWithIncompleteCheckoutError;
                }
            }
        });
        /**
     * Revert the client-side state to an individual buyer: clear the in-
     * memory identification of any company fields, strip the same fields
     * from the localStorage cache so Edit-Details re-mounts render the
     * buyer as a private customer, and tell the backend to wipe the VAT
     * identity on the incomplete-checkout record.
     */ this.removePersistedVat = flow(function*(throwIfError) {
            if (this.identification) {
                this.identification = {
                    ...this.identification,
                    customerType: ECheckoutCustomerType.Individual,
                    companyName: "",
                    street: "",
                    city: "",
                    vatId: ""
                };
            }
            // Mirror the clear to localStorage so an Edit-Details remount (which
            // reads `initialValues` from there) doesn't resurrect the company
            // state Paddle just told us to drop.
            mergeIntoMemorized({
                customerType: "individual",
                companyName: "",
                street: "",
                city: "",
                vatId: ""
            });
            try {
                const response = yield realCommerceRequest({
                    location: routeLocationPaddleIncompleteCheckoutPatch,
                    request: {
                        checkout: {
                            uuid: this.incompleteCheckout.checkout.uuid
                        },
                        vatIdRemovedEventThrown: true
                    }
                });
                // Pull the re-evaluated server state back into the local
                // checkout. Removing the VAT ID can knock the buyer back into
                // a non-reverse-charge tax bracket, change `cashbackPromotion`
                // attachment, etc. — anything the backend recomputes off the
                // updated checkout row needs to flow into the right-column
                // Order Summary on the next render.
                if (this.incompleteCheckout) {
                    this.incompleteCheckout = {
                        ...this.incompleteCheckout,
                        checkout: response.checkout
                    };
                }
            } catch (e) {
                if (throwIfError) {
                    throw e;
                }
            }
        });
        /**
     * Push the company-identity triple (customerType + VAT ID + country)
     * captured from Paddle's `Checkout.Vat.Applied` event into the
     * incomplete-checkout record so the backend knows this buyer flipped
     * to a business purchase mid-checkout. The local `identification` is
     * mirrored alongside so an Edit-Details round trip restores those
     * fields. The remaining address fields (companyName/street/city/zip)
     * are not part of Paddle's event payload and stay user-entered.
     */ this.syncCompanyAddressWithIncompleteCheckout = flow(function*(address, throwIfError) {
            if (!this.incompleteCheckout) {
                return;
            }
            // Mirror the identity triple onto the local identification so a
            // subsequent Edit-Details remount restores the form as a company
            // buyer with VAT ID + country pre-filled.
            if (this.identification) {
                this.identification = {
                    ...this.identification,
                    customerType: address.customerType,
                    country: address.country,
                    vatId: address.vatId
                };
            }
            // Persist the same identity triple into the localStorage form cache:
            // Edit-Details re-reads `initialValues` from there (the in-memory
            // identification is reset first), so without this merge the buyer
            // would see the pre-VAT "individual" state again on re-entry. The
            // enum values of `ECheckoutCustomerType` ("individual" / "company")
            // already match the form's `CustomerType` union, so the string-
            // shaped payload is compatible with the form type.
            mergeIntoMemorized({
                customerType: address.customerType === "company" ? "company" : "individual",
                country: address.country,
                vatId: address.vatId
            });
            try {
                yield realCommerceRequest({
                    location: routeLocationPaddleIncompleteCheckoutPatch,
                    request: {
                        checkout: {
                            uuid: this.incompleteCheckout.checkout.uuid,
                            companyAddress: address
                        }
                    }
                });
            } catch (e) {
                if (throwIfError) {
                    throw e;
                }
            }
        });
        /**
     * Set the identification data for this transaction. This must be done AFTER
     * the user has accepted the terms and conditions (including privacy preference, ...).
     * In our case, this is the first step in the checkout form. It also automatically sets
     * the next step in the current checkout of the product.
     *
     * @param data
     */ this.applyIdentification = flow(function*(data, terms, isInvoiceAddressGiven, disableEmailCheck) {
            const { data: { productPlan: { id: planId, price: [fullPrice], product: { id: productId }, visibility }, paddlePayLink } } = this.product;
            const payLinkUuid = paddlePayLink?.uuid || this.recoveredIncompleteCheckout?.checkout.paddlePayLink?.uuid;
            try {
                const affiliateCookie = visibility === EProductPlanVisibility.Private || this.isRecovered ? undefined : getAffiliateFromStorage();
                const incompleteCheckout = yield realCommerceRequest({
                    location: routeLocationPaddleIncompleteCheckoutPost,
                    request: {
                        disableEmailCheck,
                        checkout: {
                            productPlan: {
                                id: planId,
                                fullPrice
                            },
                            product: {
                                id: productId
                            },
                            ...payLinkUuid ? {
                                paddlePayLink: {
                                    uuid: payLinkUuid
                                }
                            } : {},
                            ...data,
                            ...terms
                        },
                        isInvoiceAddressGiven,
                        ...!affiliateCookie ? {} : affiliateCookie.type === "devowl" ? {
                            affiliateLinkView: {
                                hashid: affiliateCookie.hashId
                            }
                        } : {}
                    }
                });
                this.identification = data;
                this.incompleteCheckout = incompleteCheckout;
                this.store.setStep(CheckoutStep.IdentificationSubmitted);
            } catch (e) {
                throw new RealCommerceError(e);
            }
        });
        /**
     * The payment form got successfully submitted to the payment provider and we can wait for the
     * payment. It automatically retries after x seconds x times until the payment got completed.
     */ this.applyPayment = flow(function*(ecommerceSystemCheckoutId, currentRetry = 0) {
            this.store.setStep(CheckoutStep.PaymentProcessing);
            this.awaitingPaymentRetry = currentRetry;
            const lastCall = currentRetry >= RETRY_CHECKOUT_PATCH_RETRIES;
            try {
                const checkout = yield realCommerceRequest({
                    location: routeLocationPaddleCheckoutPatch,
                    params: {
                        retries: currentRetry,
                        lastCall
                    },
                    request: {
                        checkout: {
                            ecommerceSystemCheckoutId,
                            uuid: this.incompleteCheckout.checkout.uuid
                        }
                    }
                });
                this.incompleteCheckout = undefined;
                this.checkout = checkout;
                this.store.setStep(CheckoutStep.PaymentCompleted);
                this.store.setView("success");
            } catch (e) {
                const error = new RealCommerceError(e);
                if (error.hasErrorCode("CheckoutInPaddleWaitForPaymentConfirmation") && !lastCall) {
                    // Wait and try again
                    yield new Promise((resolve)=>setTimeout(resolve, RETRY_CHECKOUT_PATCH_WAIT));
                    yield this.applyPayment(ecommerceSystemCheckoutId, currentRetry + 1);
                } else if (error.hasErrorCode("CheckoutInPaddleNotYetProcessed")) {
                    this.notYetProcessedButShowConfirmationText = error.message;
                    this.store.setStep(CheckoutStep.PaymentPendingConfirmation);
                } else {
                    // Unknown payment-apply failure: route through the store's
                    // fatalError surface so the existing `FatalError` page picks
                    // it up. Without this, the buyer would otherwise stay stuck
                    // on the payment-processing spinner with no error message.
                    this.store.setFatalError(error);
                }
            }
        });
        runInAction(()=>{
            this.product = product;
            this.recoveredIncompleteCheckout = recovered;
        });
    }
    applyNewPrices({ initialPrice, initialPriceTax, preCouponInitialPrice, recurringPrice, recurringPriceTax, preCouponRecurringPrice, coupon }) {
        this.initialPrice = initialPrice;
        this.initialPriceTax = initialPriceTax;
        this.recurringPrice = recurringPrice;
        this.recurringPriceTax = recurringPriceTax;
        this.preCouponInitialPrice = preCouponInitialPrice;
        this.preCouponRecurringPrice = preCouponRecurringPrice;
        this.syncCouponWithIncompleteCheckout(coupon);
    }
    /**
     * Clear the applied identification so the identification form becomes
     * editable again (the "Change your details" affordance after submit).
     * Discards the incomplete-checkout reference on the client side only —
     * the backend record stays and is simply re-created on the next submit.
     *
     * Also clears every transient pricing/coupon value captured from the
     * previous Paddle session (`initialPrice`, `initialPriceTax`,
     * `recurringPrice`, `recurringPriceTax`, `lastAppliedCoupon`,
     * `lastSyncCouponWithIncompleteCheckoutError`). Without this, the
     * Order Summary on the next Payment-step entry would briefly render
     * the *old* coupon discount until Paddle's iframe fires its first
     * `updatePrices` event for the new session, producing a visible
     * flicker between submit and the new pricing landing.
     */ resetIdentification() {
        this.identification = undefined;
        this.incompleteCheckout = undefined;
        this.initialPrice = undefined;
        this.initialPriceTax = undefined;
        this.recurringPrice = undefined;
        this.recurringPriceTax = undefined;
        this.preCouponInitialPrice = undefined;
        this.preCouponRecurringPrice = undefined;
        this.lastAppliedCoupon = null;
        this.lastSyncCouponWithIncompleteCheckoutError = undefined;
        this.notYetProcessedButShowConfirmationText = undefined;
        this.awaitingPaymentRetry = 0;
        this.store.setStep(CheckoutStep.VendorReady);
    }
    /**
     * Fetch incomplete order status and return the external eCommerce checkout id.
     */ async fetchIsIncompleteCheckoutComplete() {
        try {
            return await realCommerceRequest({
                location: routeLocationPaddleIncompleteCheckoutIsCompleteGet,
                params: {
                    uuid: this.incompleteCheckout.checkout.uuid
                }
            });
        } catch (e) {
            throw new RealCommerceError(e);
        }
    }
}
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Product === "undefined" ? Object : Product)
], Transaction.prototype, "product", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof IRouteResponsePaddleCheckoutPatch === "undefined" ? Object : IRouteResponsePaddleCheckoutPatch)
], Transaction.prototype, "checkout", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof IRouteResponsePaddleIncompleteCheckoutPost === "undefined" ? Object : IRouteResponsePaddleIncompleteCheckoutPost)
], Transaction.prototype, "incompleteCheckout", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof IRouteResponsePaddleIncompleteCheckoutGet === "undefined" ? Object : IRouteResponsePaddleIncompleteCheckoutGet)
], Transaction.prototype, "recoveredIncompleteCheckout", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Transaction.prototype, "isRecovered", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Transaction.prototype, "store", null);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Exception === "undefined" ? Object : Exception)
], Transaction.prototype, "error", void 0);
_ts_decorate([
    observable
], Transaction.prototype, "awaitingPaymentRetry", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Array),
    _ts_metadata("design:paramtypes", [])
], Transaction.prototype, "awaitingPaymentRetryImageAndText", null);
_ts_decorate([
    observable,
    _ts_metadata("design:type", String)
], Transaction.prototype, "notYetProcessedButShowConfirmationText", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Transaction.prototype, "initialPrice", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Transaction.prototype, "initialPriceTax", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Transaction.prototype, "recurringPrice", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Transaction.prototype, "recurringPriceTax", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Transaction.prototype, "preCouponInitialPrice", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Transaction.prototype, "preCouponRecurringPrice", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Transaction.prototype, "identification", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof RealCommerceError === "undefined" ? Object : RealCommerceError)
], Transaction.prototype, "lastSyncCouponWithIncompleteCheckoutError", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Transaction.prototype, "hasAppliedCoupon", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], Transaction.prototype, "applyNewPrices", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], Transaction.prototype, "resetIdentification", null);
export { Transaction, RETRY_CHECKOUT_PATCH_WAIT };
